import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { T, F, I, Over } from "@/lib/bricks";
import BricksWordmark from "@/components/BricksWordmark";

const inputStyle = {
  marginTop:8, width:'100%', padding:'14px 16px',
  border:`1px solid ${T.hairStrong}`, borderRadius:2,
  fontSize:15, fontFamily:F.display, color:T.ink,
  background:'transparent', outline:'none', boxSizing:'border-box',
};

export default function ResetPasswordPage() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Password must be at least 8 characters.");
    if (password !== confirm) return setError("Passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div style={{ display:'flex', minHeight:'100vh', background:T.paper, fontFamily:F.display, color:T.ink }}>
      <div style={{ flex:1, borderRight:`1px solid ${T.hair}`, padding:'48px 56px', display:'flex', flexDirection:'column' }}>
        <BricksWordmark size={19} />

        <div style={{ marginTop:'auto', maxWidth:440 }}>
          <Over color={T.inkFaint}>Password reset</Over>
          <div style={{ fontSize:44, fontWeight:500, letterSpacing:-1.4, marginTop:12, lineHeight:1.05 }}>
            {done ? <>All done,<br/><span style={{ color:T.inkMute }}>new password set.</span></>
                  : <>Choose a new<br/><span style={{ color:T.inkMute }}>password.</span></>}
          </div>

          {!token && !done && (
            <>
              <p style={{ marginTop:24, fontSize:13, color:T.bad }}>This reset link is invalid or has expired.</p>
              <button type="button" data-testid="reset-request-new" onClick={() => navigate('/login?mode=forgot', { replace: true })}
                style={{ marginTop:16, minHeight:44, padding:'13px 20px', background:T.brick, color:'#fff', border:'none', borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%' }}
              >
                Send a new link <I name="arr" size={14} color="#fff"/>
              </button>
            </>
          )}

          {token && !done && (
            <div style={{ marginTop:32, display:'flex', flexDirection:'column', gap:14 }}>
              <div>
                <Over color={T.inkMute}>New password</Over>
                <input type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="At least 8 characters" required style={inputStyle} />
              </div>
              <div>
                <Over color={T.inkMute}>Confirm password</Over>
                <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)}
                  placeholder="Repeat password" required style={{ ...inputStyle, border:`1px solid ${T.hair}` }} />
              </div>

              {error && <p style={{ fontSize:13, color:T.bad, margin:0 }}>{error}</p>}

              <button type="submit" disabled={loading} onClick={handleSubmit}
                style={{ marginTop:8, padding:'13px 20px', background:T.brick, color:'#fff', border:'none', borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%', opacity:loading ? 0.6 : 1 }}
              >
                {loading ? '…' : 'Set new password'}
                {!loading && <I name="arr" size={14} color="#fff"/>}
              </button>
            </div>
          )}

          {done && (
            <div style={{ marginTop:32, display:'flex', flexDirection:'column', gap:14 }}>
              <button onClick={() => navigate('/login', { replace: true })}
                style={{ padding:'13px 20px', background:T.brick, color:'#fff', border:'none', borderRadius:2, fontSize:14, fontWeight:500, fontFamily:F.display, cursor:'pointer', display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, width:'100%' }}
              >
                Sign in <I name="arr" size={14} color="#fff"/>
              </button>
            </div>
          )}
        </div>

        <div style={{ marginTop:'auto', fontSize: 12, color:T.inkFaint }}>
          Bricks
        </div>
      </div>

      <div className="hidden lg:block" style={{ flex:1, position:'relative', overflow:'hidden' }}>
        <div style={{ width:'100%', height:'100%', background:T.paperDeep, backgroundImage:`repeating-linear-gradient(135deg, transparent 0 10px, ${T.hair} 10px 11px)` }} />
      </div>
    </div>
  );
}
